import React, { useState } from "react";
import { Play, Download, Video as VideoIcon, CalendarDays, Clock } from "lucide-react";
import { cn } from "@/lib/utils";

// A single finished-video tile. Radius is capped at 8px (rounded-lg) and there
// are NO nested cards. Play + Download are rendered ONLY for available videos;
// expired videos remain visible as history (created date + "Expired", no
// available-until date, no actions).
const formatDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    try {
        return new Intl.DateTimeFormat("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        }).format(d);
    } catch (_e) {
        return null;
    }
};

export const VideoLibraryCard = React.forwardRef(function VideoLibraryCard(
    { video, onPlay, onDownload, highlighted = false },
    ref,
) {
    const {
        job_id: jobId,
        title,
        thumbnail_url: thumbnailUrl,
        created_at: createdAt,
        available_until: availableUntil,
        retention_status: retentionStatus,
    } = video;
    const isAvailable = retentionStatus === "available";
    const [imgFailed, setImgFailed] = useState(false);
    const showThumb = !!thumbnailUrl && !imgFailed;
    const created = formatDate(createdAt);
    const until = formatDate(availableUntil);
    const displayTitle = title || "Untitled video";

    return (
        <div
            ref={ref}
            tabIndex={-1}
            data-testid={`video-card-${jobId}`}
            data-job-id={jobId}
            data-highlighted={highlighted ? "true" : "false"}
            className={cn(
                "flex flex-col overflow-hidden rounded-lg border bg-surface-panel",
                "outline-none scroll-mt-28 transition-shadow duration-300",
                highlighted
                    ? "border-emerald-400/70 ring-2 ring-emerald-400/60"
                    : "border-white/10",
            )}
        >
            <div className="relative aspect-video w-full overflow-hidden bg-neutral-900">
                {showThumb ? (
                    <img
                        src={thumbnailUrl}
                        alt={`Thumbnail for ${displayTitle}`}
                        loading="lazy"
                        onError={() => setImgFailed(true)}
                        data-testid={`video-card-${jobId}-thumb`}
                        className="h-full w-full object-cover"
                    />
                ) : (
                    <div
                        data-testid={`video-card-${jobId}-placeholder`}
                        className="flex h-full w-full items-center justify-center text-white/20"
                        aria-hidden="true"
                    >
                        <VideoIcon className="h-10 w-10" />
                    </div>
                )}
                {!isAvailable && (
                    <span
                        data-testid={`video-card-${jobId}-expired-badge`}
                        className="absolute right-2 top-2 rounded-md bg-black/70 px-2 py-1 text-xs font-semibold text-white/70"
                    >
                        Expired
                    </span>
                )}
            </div>

            <div className="flex flex-1 flex-col gap-2 p-4">
                <p
                    data-testid={`video-card-${jobId}-title`}
                    title={displayTitle}
                    className="truncate text-sm font-semibold text-white/90"
                >
                    {displayTitle}
                </p>

                <div className="flex flex-col gap-1 text-xs">
                    {created && (
                        <span
                            data-testid={`video-card-${jobId}-created`}
                            className="inline-flex items-center gap-1.5 text-white/45"
                        >
                            <CalendarDays className="h-3.5 w-3.5 flex-none" />
                            Created {created}
                        </span>
                    )}
                    {isAvailable ? (
                        until && (
                            <span
                                data-testid={`video-card-${jobId}-available-until`}
                                className="inline-flex items-center gap-1.5 text-cyan-200/70"
                            >
                                <Clock className="h-3.5 w-3.5 flex-none" />
                                Available until {until}
                            </span>
                        )
                    ) : (
                        <span
                            data-testid={`video-card-${jobId}-status`}
                            className="inline-flex items-center gap-1.5 text-white/40"
                        >
                            <Clock className="h-3.5 w-3.5 flex-none" />
                            Expired
                        </span>
                    )}
                </div>

                {isAvailable && (
                    <div className="mt-2 flex items-center gap-2">
                        <button
                            type="button"
                            data-testid={`video-card-${jobId}-play`}
                            onClick={(e) => onPlay && onPlay(video, e.currentTarget)}
                            className={cn(
                                "inline-flex flex-1 items-center justify-center gap-2 min-h-11 rounded-lg px-3 text-sm font-semibold",
                                "bg-purple-500/90 text-white transition-colors hover:bg-purple-500",
                                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/60",
                            )}
                        >
                            <Play className="h-4 w-4" />
                            Play
                        </button>
                        <button
                            type="button"
                            data-testid={`video-card-${jobId}-download`}
                            onClick={() => onDownload && onDownload(video)}
                            className={cn(
                                "inline-flex flex-1 items-center justify-center gap-2 min-h-11 rounded-lg px-3 text-sm font-semibold",
                                "border border-cyan-300/40 text-cyan-100 transition-colors hover:border-cyan-300/70 hover:text-white",
                                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60",
                            )}
                        >
                            <Download className="h-4 w-4" />
                            Download
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
});
